import React, { Dispatch, SetStateAction } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import { Spinner } from 'src/components/atoms';
import { CancelModal } from 'src/components/molecules/CancelModal';
import { ArchiveFormConfirmModal } from 'src/components/pages/CoiFormManagement/ArchiveFormConfirmModal';
import { DuplicateFormModal } from 'src/components/pages/CoiFormManagement/DuplicateFormModal';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import { PageLoadingOverlay } from '../../FormCreation.styles';

interface FormCreationModalsProps {
  showCancellationModal: boolean;
  hasUnsavedChanges: boolean;
  preventNavigation: boolean;
  setNextLocation: Dispatch<SetStateAction<{ pathname: string }>>;
  setShowCancellationModal: Dispatch<SetStateAction<boolean>>;
  onLeave: () => void;
  onStay: () => void;
  showPublishConfirmModal: boolean;
  setShowPublishConfirmModal: Dispatch<SetStateAction<boolean>>;
  handlePublishConfirm: () => void;
  showDuplicateModal: boolean;
  setShowDuplicateModal: Dispatch<SetStateAction<boolean>>;
  handleDuplicateConfirm: (
    mode: 'all-languages' | 'default-only',
    newTitle: string,
  ) => void;
  isDuplicating: boolean;
  showArchiveModal: boolean;
  setShowArchiveModal: Dispatch<SetStateAction<boolean>>;
  handleArchiveConfirm: () => void;
  handleArchiveCancel: () => void;
  currentAction: 'draft' | 'publish' | null;
  formType: FormType;
}

export const FormCreationModals: React.FC<FormCreationModalsProps> = ({
  showCancellationModal,
  hasUnsavedChanges,
  preventNavigation,
  setNextLocation,
  setShowCancellationModal,
  onLeave,
  onStay,
  showPublishConfirmModal,
  setShowPublishConfirmModal,
  handlePublishConfirm,
  showDuplicateModal,
  setShowDuplicateModal,
  handleDuplicateConfirm,
  isDuplicating,
  showArchiveModal,
  setShowArchiveModal,
  handleArchiveConfirm,
  handleArchiveCancel,
  currentAction,
  formType,
}) => (
  <>
    <CancelModal
      size="small"
      title="Discard unsaved changes?"
      description="Your draft contains unsaved changes. Do you want to discard the changes and leave?"
      showCancellationModal={showCancellationModal}
      preventNavigation={!!hasUnsavedChanges && preventNavigation}
      setNextLocation={setNextLocation}
      setOpen={setShowCancellationModal}
      setShowCancellationModal={setShowCancellationModal}
      onCancel={onLeave}
      onClose={onStay}
      onConfirm={onStay}
      cancelButtonText="Discard and leave"
      confirmButtonText="Continue editing"
      data-testid="unsaved-changes-modal"
    />
    <Dialog
      open={showPublishConfirmModal}
      onClose={() => setShowPublishConfirmModal(false)}
      aria-labelledby="publish-form-dialog-title"
      aria-describedby="publish-form-dialog-description"
      data-testid="publish-confirmation-modal"
    >
      <DialogTitle component="div">
        <Typography variant="h6" component="h2" id="publish-form-dialog-title">
          Publish form
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={() => setShowPublishConfirmModal(false)}
          size="small"
          color="inherit"
          data-testid="publish-confirmation-modal-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="publish-form-dialog-description">
          When you publish the form, it will appear in Reporter Portal.
          Employees will be able to create reports with this form. Do you want
          to publish this form?
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => setShowPublishConfirmModal(false)}
          data-testid="publish-confirmation-modal-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePublishConfirm}
          autoFocus
          data-testid="publish-confirmation-modal-confirm"
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
    <DuplicateFormModal
      open={showDuplicateModal}
      setOpen={setShowDuplicateModal}
      onConfirm={handleDuplicateConfirm}
      isLoading={isDuplicating}
      formType={formType}
      data-testid="form-creation-duplicate-modal"
    />
    <ArchiveFormConfirmModal
      open={showArchiveModal}
      setOpen={setShowArchiveModal}
      onConfirm={handleArchiveConfirm}
      onCancel={handleArchiveCancel}
      data-testid="form-creation-archive-modal"
    />
    {currentAction && (
      <PageLoadingOverlay data-testid="form-creation-loading-overlay">
        <Spinner variant="secondary" size="xlarge" />
      </PageLoadingOverlay>
    )}
  </>
);
